//eslint:disable max-func-body-length
import * as React from 'react';

import { PromptConfirmation, ValidationProps } from 'ui';
import { STATE } from 'app/constants';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { routeConstants } from 'app/routes/route-constants';
import * as navigate from 'app/area/shared/navigation';
import { PromptProps, withPrompt } from 'app/components';
import {
  CodetableAdministrationState,
  CodeFieldIncludeInactiveKey,
  CodeFieldWireKey
} from '../types-store';
import { CodetableSelectorProps } from '../types';
import { indicatorFactorsAdministrationTranslation } from '../constants';
import { selectors, getCodetableMetadata } from '../selectors';
import { actionCreators } from './actions';
import styles from './codetable-list.scss';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { Breadcrumb } from 'ui/common/breadcrumb';
import { BreadcrumbValue } from 'ui/common/breadcrumb/breadcrumb-item';
import { deepEquals } from 'app/infrastructure/js';
import { buildBreadcrumbs, buildGoBackBreadcrumbs, getCodetableId } from 'app/area/shared/codetable/breadcrumbs';
import { codeFieldIncludeInactive } from 'app/area/indicator-factors/common/codetable-options-handler/constants';
import {
  codeDefaultFilters,
  codeFilters, codePredefinedFilters
} from 'app/area/indicator-factors/list/search-filters/code-filter-configuration';
import { SerializePayload } from 'app/area/search-filters/types';
import { serialize } from 'app/area/shared/codetable/serialize/serialize';
import { codeSearchArea, ShouldHideFiltersDrawerPayload } from 'app/area/indicator-factors/list/types';
import isArray from 'lodash/isArray';
import { IndicatorFactorsListComponent } from 'app/area/indicator-factors/list/indicator-factors-list';
import { DuplicateRowsModal } from 'app/area/indicator-factors/list/duplicate-rows/duplicate-rows';
import { CodetableItemLoader } from 'app/area/shared/codetable/item/codetable-item-loader';
import { FilterSearchModel } from 'app/area/search-filters/types-store';
import { codePickersId } from 'app/area/indicator-factors/item/code-pickers/types';
import { DeleteRowsModal } from 'app/area/indicator-factors/list/delete-rows/delete-rows';
import { ToolbarActions } from 'app/area/indicator-factors/list/toolbar-actions/toolbar-actions';
import { CodeComplexValue } from 'app/area/shared/codetable/types';
import { scrollToTop } from 'app/infrastructure/platform/routing';

export type IndicatorFactorsListProps = DefaultContainerProps & CodetableAdministrationState &
  CodetableSelectorProps & PromptProps & typeof actionCreators;

export interface IndicatorFactorsListState {
  breadcrumbs: CodeComplexValue<number>[];
  duplicateRows: boolean;
  deleteRows: boolean;
  cancelChanges: boolean;
}

class IndicatorFactorsListPure extends React.PureComponent<IndicatorFactorsListProps, IndicatorFactorsListState> {
  public static statePath: string = STATE.indicatorFactors;

  public state: IndicatorFactorsListState = {
    breadcrumbs: [],
    duplicateRows: false,
    deleteRows: false,
    cancelChanges: false
  };

  public componentDidMount() {
    scrollToTop();
    this.loadCategoryOptions();
    this.loadNewState(true);
  }

  public componentDidUpdate(prevProps: IndicatorFactorsListProps) {
    this.loadNewState(false, prevProps);
  }

  private toggleDuplicateRows = () => {
    this.setState({ duplicateRows: !this.state.duplicateRows });
    this.props.clearDuplicateRows();
  };

  private toggleDeleteRows = () => {
    this.setState({ deleteRows: !this.state.deleteRows });
  };

  private cancelChanges = () => {
    this.setState({ cancelChanges: !this.state.cancelChanges });
  };

  private continueCancelChanges = () => {
    this.props.cancelChanges();
    this.cancelChanges();
  };

  private updateSearchModel = (payload: SerializePayload) => {
    const { list: { requestedSearchModel, category } = {},
      access: { data: accessData } = {},
      loadCodeItems, updateSearchModel,
      categoryId } = this.props;
    const newSearchModel = serialize(payload);

    if (!deepEquals(newSearchModel, requestedSearchModel)) {

      const codetableId = getCodetableId(this.props.breadcrumbs);

      loadCodeItems({
        codetableId,
        parentId: undefined,
        categoryId: category ? category.value : isDefinedIncludingZero(categoryId) ? categoryId : undefined,
        searchModel: newSearchModel,
        startIndex: undefined,
        maxResults: undefined,
        includeInactiveCodes: this.getCurrentIncludeInactive(),
        ignoreCategoryFilters: false,
        isCentralAdmin: !!accessData?.codetables?.administrationCentral
      });

      updateSearchModel(newSearchModel);
    }
  };

  private getCurrentIncludeInactive = () => codeFieldIncludeInactive[this.props.list.includeInactive];

  private loadNewState = (reload: boolean, prevProps?: IndicatorFactorsListProps) => {
    const { metadata: { data },
      list: { fetchingCodetableId, requestedSearchModel, category, fetchingBreadcrumbsCodetableId },
      access: { data: accessData } = {},
      loadCodeItems, loadCodetableAdministrationList, updateIncludeInactive, categoryId
    } = this.props;

    const codetableId = getCodetableId(this.props.breadcrumbs);

    const codetableMetadata = getCodetableMetadata(data, codetableId);

    if (!codetableMetadata) {
      navigate.replace(routeConstants.indicatorFactorsAdministrationFor(this.props.breadcrumbs));
      return;
    }

    if (this.shouldBuildBreadcrumbs(reload, prevProps)) {
      const breadcrumbs = buildBreadcrumbs(this.props.list.options, this.props.breadcrumbs);
      this.setState({ breadcrumbs });
    }

    if (!!codetableId && fetchingCodetableId !== codetableId &&
      (!prevProps || prevProps.list?.fetchingCodetableId !== fetchingCodetableId)) {
      this.props.resetListState();
      !isDefinedIncludingZero(fetchingBreadcrumbsCodetableId) && loadCodetableAdministrationList();
      updateIncludeInactive(CodeFieldIncludeInactiveKey.active);
      return;
    }

    if (reload) {
      loadCodeItems({
        codetableId,
        parentId: undefined,
        categoryId: category ? category.value : isDefinedIncludingZero(categoryId) ? categoryId : undefined,
        searchModel: requestedSearchModel,
        startIndex: undefined,
        maxResults: undefined,
        includeInactiveCodes: this.getCurrentIncludeInactive(),
        ignoreCategoryFilters: false,
        isCentralAdmin: !!accessData?.codetables?.administrationCentral
      });
    }
  };

  private loadMoreData = (searchModel: FilterSearchModel, startIndex: number) => {
    const codetableId = getCodetableId(this.props.breadcrumbs);
    this.props.loadCodeItems({
      codetableId,
      parentId: undefined,
      categoryId: undefined,
      searchModel,
      startIndex,
      maxResults: undefined,
      includeInactiveCodes: this.getCurrentIncludeInactive(),
      ignoreCategoryFilters: false,
      isCentralAdmin: !!this.props.access.data?.codetables?.administrationCentral
    });
  };

  private loadCategoryOptions = () => {
    const { loadCodetableOptions,
      metadata: { data: metadataValue },
      router: { location: { query: { categoryId } = {} } = {} } }
      = this.props;
    if (categoryId && !!metadataValue && !!metadataValue.codetableCategory && isDefinedIncludingZero(metadataValue.codetableCategory.id)) {
      loadCodetableOptions(CodeFieldWireKey.category, metadataValue.codetableCategory.id, {});
    }
  };

  private shouldBuildBreadcrumbs = (reload: boolean, prevProps: IndicatorFactorsListProps) => this.props &&
    this.props.list &&
    this.props.list.options && (!prevProps || prevProps && prevProps.list && !deepEquals(prevProps.list.options, this.props.list.options))
    && this.props.list.options && this.props.list.options.length > 0 && this.props.breadcrumbs && this.props.breadcrumbs.length > 0;

  private hasSelectedCategory = () => {
    const { list: { category } } = this.props;
    return category && isDefinedIncludingZero(category.value);
  };

  private isFetching = () => {
    const { access: { fetchingCodetables } = {} } = this.props;
    return !!fetchingCodetables;
  };

  private goBackToCodeSelector = (item: BreadcrumbValue) =>
    navigate.toCodeSelectView(buildGoBackBreadcrumbs(item, this.props.breadcrumbs));

  private goBackToHomeCodeSelector = () => navigate.to(routeConstants.selectCodetableAdministration);

  private codetableMetadata = () => {
    const { metadata: { data } } = this.props;

    return getCodetableMetadata(data, getCodetableId(this.props.breadcrumbs));
  };

  private shouldRenderSpinLoader = () => {
    const { list: { data } = {}, access: { fetchingCodetables: accessFetching } = {} } = this.props;
    return !!accessFetching || !data;
  };

  private shouldHideFiltersDrawer = (payload: ShouldHideFiltersDrawerPayload) => {
    const { customFilters, queryFilters, predefinedFilters } = payload;
    return !(this.hasAnyFilterDefined(customFilters) ||
      this.hasAnyFilterDefined(queryFilters) || this.hasAnyFilterDefined(predefinedFilters?.values));
  };

  private getParametersId = (validation: {
    [key: string]: ValidationProps;
  }) => validation?.code?.messages[0]?.parameters?.id;

  private loadCodeParameters = (validation: {
    [key: string]: ValidationProps;
  }) => {
    this.props.loadCodeParameters({
      codetableId: codePickersId.indicatorFactors,
      text: this.getParametersId(validation)?.join()
    });
  };

  private duplicateRows = () => {
    this.props.duplicateRows(() => {
      this.loadNewState(true);
      this.toggleDuplicateRows();
    }, this.loadCodeParameters);
  };

  private deleteRows = () => {
    this.props.deleteRows(
      getCodetableId(this.props.breadcrumbs),
      this.props?.list,
      this.props?.translation,
      () => {
        this.loadNewState(true);
        this.toggleDeleteRows();
      });
  };

  private hasAnyFilterDefined = (array: object[]) => !!array && isArray(array) && array.length > 0;

  private saveChanges = () => {
    const { list: { modifiedFactors, modifiedComments }, saveFactors, saveComments, translation } = this.props;

    modifiedFactors.length > 0 && saveFactors(modifiedFactors,
      translation.message_codes_updated,
      translation.validation_error);

    modifiedComments.length > 0 && saveComments(modifiedComments,
      translation.message_codes_updated,
      translation.validation_error);
  };

  public render() {

    const { list: { category, fetchingBreadcrumbsCodetableId,
      fetchingCodetableId, data, numberOfResults, itemAccesses } = {}, translation,
      shouldDisplayModal, continue: onContinue, cancel } = this.props;
    const { breadcrumbs } = this.state;

    const hasSelectedCategory = this.hasSelectedCategory();
    const codetableId = getCodetableId(this.props.breadcrumbs);

    const shouldRenderList = !!data && data.length > 0 && numberOfResults > 0
      && (this.props.access && !this.props.access.fetchingCodetables);

    const shouldRenderBreadcrumbs = !fetchingBreadcrumbsCodetableId && breadcrumbs && breadcrumbs.length > 0;
    const metadata = this.codetableMetadata();
    const queryFilters = undefined;
    const predefinedFilters = codePredefinedFilters(translation);
    const customFilters = codeFilters(metadata, translation);
    const defaultFilterValues = codeDefaultFilters(translation);

    const hideDrawer = this.shouldHideFiltersDrawer({ queryFilters, predefinedFilters, customFilters });

    const { access: { data: accessData } = {} } = this.props;
    const canCreate = !!accessData?.codetables?.create;

    const topToolbar = <div className="col-12">
      <div className={styles.topToolbarRow}>
        <div className={styles.left}>
          {fetchingBreadcrumbsCodetableId ? <CodetableItemLoader /> : null}
          {shouldRenderBreadcrumbs
            ? <Breadcrumb
              items={breadcrumbs || []}
              onClickItem={this.goBackToCodeSelector}
              home={true}
              homeLabel={translation.codetable_administration}
              onClickBreadcrumbHome={this.goBackToHomeCodeSelector} />
            : <div className={styles.breadcrumbPlaceholder} />}
        </div>
        <ToolbarActions
          codetableId={codetableId}
          canCreate={canCreate}
          breadcrumbs={this.props.breadcrumbs}
          fetchingCodetableId={fetchingCodetableId}
          toggleDuplicateRows={this.toggleDuplicateRows}
          toggleDeleteRows={this.toggleDeleteRows}
          numberOfResults={this.props.list.numberOfResults}
          translation={translation} />
      </div>
    </div>;

    return <>
      {shouldDisplayModal
        ? <PromptConfirmation
          title={translation.cancel_changes}
          description={translation.are_you_sure}
          continueText={translation.undo_all}
          cancelText={translation.go_back}
          onContinue={onContinue}
          onCancel={cancel}
        />
        : ''}

      {this.state.cancelChanges
        ? <PromptConfirmation
          title={translation.cancel_changes}
          description={translation.are_you_sure}
          continueText={translation.undo_all}
          cancelText={translation.go_back}
          onContinue={this.continueCancelChanges}
          onCancel={this.cancelChanges}
        />
        : ''}

      <IndicatorFactorsListComponent
        {...this.props}
        {...this.props.list}
        codetableAccess={accessData}
        itemAccesses={itemAccesses}

        isFetching={this.isFetching()}
        codetableId={getCodetableId(this.props.breadcrumbs)}
        category={category}
        metadata={metadata}
        shouldRenderSpinLoader={this.shouldRenderSpinLoader()}
        shouldRenderList={shouldRenderList}
        hasCategoryMetadata={false}
        hasSelectedCategory={hasSelectedCategory}
        hideDrawer={hideDrawer}
        hideSearch={true}
        wideScreen={true}
        topToolbar={topToolbar}
        filtersConfig={{ area: codeSearchArea, ...(isDefinedIncludingZero(codetableId) && { customConfig: { codetableId } }) }}
        queryFilters={queryFilters}
        predefinedFilters={predefinedFilters}
        customFilters={customFilters}
        defaultFilterValues={defaultFilterValues}
        updateSearchModel={this.updateSearchModel}
        loadMoreData={this.loadMoreData}
        saveChanges={this.saveChanges}
        cancelChanges={this.cancelChanges}
        locale={this.props.sharedState.locale}
        displayInfoText={true} />

      {this.state.duplicateRows && <DuplicateRowsModal
        onClose={this.toggleDuplicateRows}
        save={this.duplicateRows}
        locale={this.props.sharedState.locale}
        validation={this.props.list?.duplicateRows?.validation}
        {...this.props}
      />}

      {this.state.deleteRows && <DeleteRowsModal
        fetchingDeleteRows={this.props.list?.fetchingDeleteRows}
        numberOfResults={numberOfResults}
        onClose={this.toggleDeleteRows}
        save={this.deleteRows}
        translation={translation} />}
    </>;
  }
}

const container = containerFactory(
  withPrompt(IndicatorFactorsListPure),
  actionCreators, selectors, indicatorFactorsAdministrationTranslation);

export { container as IndicatorFactorsList, IndicatorFactorsListPure as CodetableListPure };
